<template>
  <div class="action-details-page" style="margin-top: 8px">
    <CollapsibleSection v-if="action" title="Action Details" :collapsible="false">
      <ActionDetails
        :action="action"
        :workflowName="workflowName"
        :projectID="projectID"
        :projectName="projectName"
        @updateAction="updateActionDetails"
      />
    </CollapsibleSection>

    <CollapsibleSection v-if="comments" title="Comments">
      <ExperimentComments
        :comments="comments"
        :users="users"
        :files="files"
        :entityId="action.id"
        @addComment="addComment"
        @removeComment="removeComment"
        @updateComment="handleUpdateComment"
      />
    </CollapsibleSection>

    <CollapsibleSection v-if="action && files" title="Files">
      <ExperimentFiles
        ref="filesComp"
        :files="files"
        :folders="folders"
        :base-path="filesBasePath"
        :loadingTree="isFilesTreeLoading"
        :experimentId="action.id"
        :assId="action.id"
        :assType="'action'"
        :isUploading="isUploading"
        :uploadPercent="uploadPercent"
        :uploadLabel="uploadLabel"
        @addFiles="addFiles"
        @removeFile="removeFile"
      />
    </CollapsibleSection>

    <FileUploadSession
      :visible="uploadSession.visible"
      :fileType="uploadSession.fileType"
      :entityId="uploadSession.entityId"
      :files="uploadSession.files"
      :entraId="loggedInUser?.entra_id"
      :folderId="uploadSession.folderId"
      @complete="handleUploadComplete"
      @close="handleUploadClose"
      @error="handleUploadError"
    />

    <CollapsibleSection v-if="action" title="Experiments">
      <ExperimentList 
        :experiments="experiments" 
        :actionId="action.id"
        :projectId="projectID"
        :workflowName="workflowName"
        :projectName="projectName"
        :actionName="action.name"
        :locationId="action.location_id"
      />
    </CollapsibleSection>

  
  </div>
</template>

<script>
import ActionDetails from '@/components/ActionDetails.vue';
import ExperimentFiles from '@/components/ExperimentFiles.vue';
import ExperimentComments from '@/components/ExperimentComments.vue';
import ExperimentList from '@/components/ExperimentList.vue';
import FileUploadSession from '@/components/FileUploadSession.vue';
import CollapsibleSection from '@/components/CollapsibleSection.vue';
import { useEntityDiscussion } from '@/composables/useEntityDiscussion';
import axios from 'axios';
import { parseFilesTree } from '@/utils/filesTree';
import { API_ENDPOINTS } from '@/services/config';
import apiService from '@/services/apiService';
import { useToast } from 'vue-toastification';
import { mapState } from 'vuex';

export default {
  components: {
    ActionDetails,
    ExperimentFiles,
    ExperimentComments,
    ExperimentList,
    FileUploadSession,
    CollapsibleSection,
  },
  data() {
    return {
      action: null,
      experiments: [],
      files: [],
      folders: [],
      isFilesTreeLoading: false,
      filesBasePath: '',
      comments: [],
      users: [],
      workflowName: '',
      projectID: null,
      projectName: '',
      isUploading: false,
      uploadPercent: 0,
      uploadLabel: "",
      uploadSession: {
        visible: false,
        fileType: '',
        entityId: null,
        files: [],
        folderId: null,
      },
    };
  },
  setup() {
    const toast = useToast();
    const discussion = useEntityDiscussion();
    return { toast, discussion };
  },
  created() {
    // Extract properties from route query
    this.workflowName = this.$route.query.workflowName || '';
    this.projectID = this.$route.query.projectID ? Number(this.$route.query.projectID) : null;
    this.projectName = this.$route.query.projectName || '';

    this.fetchActionData();
    this.fetchComments();
    this.fetchUsers();
    this.fetchExperiments();
  },
  computed: {
    ...mapState(['loggedInUser']), 
  },
  methods: {
    async fetchActionData() {
      const actionId = this.$route.params.id;
      try {
        const response = await axios.get(API_ENDPOINTS.action(actionId), {
          params: { entra_id: this.loggedInUser.entra_id }
        });
        this.action = response.data;
        this.fetchFiles();
      } catch (error) {
        this.toast.error('Failed to fetch action data');
        console.error('Failed to fetch action data:', error);
      }
    },

    async fetchFiles() {
      const actionId = this.action.id;
      this.isFilesTreeLoading = true;
      try {
        const response = await axios.get(API_ENDPOINTS.filesTree, {
          params: {
            file_type: 'action',
            entity_id: actionId,
            entra_id: this.loggedInUser.entra_id,
          },
        });
        const { files, folders, basePath } = parseFilesTree(response.data);
        this.files = files;
        this.folders = folders;
        this.filesBasePath = basePath;
      } catch (error) {
        this.files = [];
        this.folders = [];
        this.filesBasePath = '';
        console.error('Error fetching files/folders:', error);
      } finally {
        this.isFilesTreeLoading = false;
      }
    },
    async fetchComments() {
      try {
        this.comments = await this.discussion.fetchComments({
          entityType: 'action',
          entityId: this.$route.params.id,
          entraId: this.loggedInUser.entra_id,
        });
      } catch (error) {
        this.toast.error('Failed to fetch comments');
        console.error('Failed to fetch comments:', error);
      }
    },

    async fetchExperiments() {
      const actionId = this.$route.params.id;
      try {
        const response = await axios.get(API_ENDPOINTS.actionExperiments(actionId), {
          params: {
            entra_id: this.loggedInUser.entra_id,
          },
        });
        console.log('[ActionDetailPage] actionExperiments payload:', response.data);
        if (Array.isArray(response.data) && response.data.length > 0) {
          console.log('[ActionDetailPage] first action experiment row:', response.data[0]);
        }
        this.experiments = response.data;
      } catch (error) {
        if (error.response.status === 404) {
          this.experiments = [];
        } else {
          this.toast.error('Failed to fetch experiments');
          console.error('Failed to fetch experiments:', error);
        }
      }
    },

    async fetchUsers() {
      try {
        this.users = await this.discussion.fetchUsers({
          entraId: this.loggedInUser.entra_id,
        });
      } catch (error) {
        this.toast.error('Failed to fetch users');
        console.error('Failed to fetch users:', error);
      }
    },

    async addFiles({ files, id, folderId, fileType }) {
      this.uploadSession = {
        visible: true,
        fileType: fileType || 'action',
        entityId: id,
        files: Array.from(files),
        folderId: folderId || null,
      };
    },
    async handleUploadComplete() {
      await this.fetchFiles();
      if (this.$refs.filesComp?.fetchFolders) {
        await this.$refs.filesComp.fetchFolders();
      }
      this.uploadSession.visible = false;
    },
    handleUploadClose() {
      this.uploadSession.visible = false;
      this.fetchFiles();
    },
    handleUploadError(error) {
      console.error('Upload session error:', error);
    },

    async removeFile(fileId) {
      try {
        await apiService.deleteFile(fileId, this.loggedInUser?.id, this.loggedInUser?.entra_id);
        this.files = this.files.filter(file => file.id !== fileId);
        this.toast.success('File removed successfully!');
      } catch (error) {
        this.toast.error('Failed to remove file');
        console.error('Failed to remove file:', error);
      }
    },

    async addComment(commentText, author_id, files) {
      try {
        await this.discussion.addComment({
          entityType: 'action',
          entityId: this.$route.params.id,
          text: commentText,
          authorId: author_id,
          files,
          entraId: this.loggedInUser.entra_id,
        });

        if (files && files.length > 0) {
          await this.fetchFiles();
        }
        await this.fetchComments();
        this.toast.success('Comment added successfully!');
      } catch (error) {
        this.toast.error('Failed to add comment');
        console.error('Failed to add comment:', error);
      }
    },

    async removeComment(commentId) {
      try {
        await this.discussion.removeComment({
          commentId,
          userId: this.loggedInUser.id,
          entraId: this.loggedInUser.entra_id,
        });
        await this.fetchComments();
        await this.fetchFiles();
        this.toast.success('Comment removed successfully!');
      } catch (error) {
        this.toast.error('Failed to remove comment');
        console.error('Failed to remove comment:', error);
      }
    },

    async updateActionDetails(updatedAction) {
      const actionId = this.$route.params.id;
      try {
        const response = await apiService.updateResource(`${API_ENDPOINTS.actions}/${actionId}`, updatedAction, this.loggedInUser.id, this.loggedInUser.entra_id);
        this.action = response.data;
        this.fetchActionData();
        this.toast.success('Action updated successfully!');
      } catch (error) {
        this.toast.error('Failed to update action');
        console.error('Failed to update action:', error);
      }
    },

    async handleUpdateComment(commentId, newText, Id) {
      try {
        await this.discussion.updateComment({
          commentId,
          entityType: 'action',
          entityId: Id ?? this.$route.params.id,
          text: newText,
          userId: this.loggedInUser.id,
          entraId: this.loggedInUser.entra_id,
        });
        await this.fetchComments();
        this.toast.success("Comment updated successfully!");
      } catch (error) {
        this.toast.error('Failed to update comment');
        console.error('Failed to update comment:', error);
      }
    },
  },
};
</script>
  
  <style scoped>
  .tabs {
    display: flex;
    justify-content: center;
    margin-top: 10px;
    gap: 15px;
  }
  </style>
  